import { Form, Formik } from 'formik';
import moment from 'moment';
import { useState } from 'react';
import * as Yup from 'yup';
import ModalComponent from '../../../../components/modal/modal';
import ModalBodyComponent from '../../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../../components/modal/modalFooterComponent';
import CustomDatePicker from '../../../../components/date-picker/customDatePicker';
import FormSelect from '../../../../components/dropdown/formSelect';
import Heading from '../../../../components/typography';
import { COLORS } from '../../../../constants/style/color';
import {
  calculateEffectiveTo,
  EXPIRATION_PRESET_OPTIONS,
  type ExpirationPreset,
} from '../expirationUtils';
import AdvisorApiKeyRevealContent from './AdvisorApiKeyRevealContent';

export interface AdvisorApiKeyFormValues {
  effectiveFrom: string;
  expirationPreset: ExpirationPreset;
  effectiveTo: string;
}

export interface CreatedAdvisorApiKey {
  apiKey: string;
  apiKeyPrefix?: string | null;
}

interface CreateAdvisorApiKeyModalProps {
  isOpen: boolean;
  isLoading: boolean;
  createdApiKey?: CreatedAdvisorApiKey | null;
  onClose: () => void;
  onSubmit: (values: AdvisorApiKeyFormValues) => Promise<CreatedAdvisorApiKey | null | undefined>;
}

const validationSchema = Yup.object().shape({
  effectiveFrom: Yup.string().required('Effective from is required'),
  expirationPreset: Yup.string().required('Expiration is required'),
  effectiveTo: Yup.string()
    .nullable()
    .when('expirationPreset', {
      is: 'custom',
      then: (schema) =>
        schema
          .required('End date is required for a custom expiration')
          .test('after-effective-from', 'End date must be on or after effective from', function (value) {
            const { effectiveFrom } = this.parent as AdvisorApiKeyFormValues;
            if (!value || !effectiveFrom) return true;
            return moment(value).isSameOrAfter(moment(effectiveFrom), 'day');
          }),
      otherwise: (schema) => schema.nullable(),
    }),
});

const initialValues: AdvisorApiKeyFormValues = {
  effectiveFrom: moment().format('YYYY-MM-DD'),
  expirationPreset: '1_year',
  effectiveTo: '',
};

const CreateAdvisorApiKeyModal = ({
  isOpen,
  isLoading,
  createdApiKey,
  onClose,
  onSubmit,
}: CreateAdvisorApiKeyModalProps): JSX.Element => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return <></>;

  const isRevealing = createdApiKey != null;
  const isBusy = isLoading || isSubmitting;

  const handleFormSubmit = async (values: AdvisorApiKeyFormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...values,
        effectiveTo: calculateEffectiveTo(
          values.effectiveFrom,
          values.expirationPreset,
          values.effectiveTo,
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalComponent
      id={isRevealing ? 'advisorApiKeyRevealModal' : 'createAdvisorApiKeyModal'}
      show={isOpen}
      closeModal={isBusy ? () => {} : onClose}
      modalStatus={isRevealing ? 'View' : undefined}
      title={isRevealing ? 'Save Your Advisor API Key' : 'Generate Advisor API Key'}
      subTitle={
        isRevealing
          ? 'Copy this key now. You will not be able to view it again.'
          : 'Set when the new integration key becomes active and when it expires.'
      }
      size={isRevealing ? 'lg' : 'md'}
      outsideClickable={false}>
      {isRevealing ? (
        <div id='advisorApiKeyRevealStep'>
          <ModalBodyComponent>
            <AdvisorApiKeyRevealContent
              apiKey={createdApiKey.apiKey}
              apiKeyPrefix={createdApiKey.apiKeyPrefix}
            />
          </ModalBodyComponent>
          <ModalFooterComponent
            onCloseModal={onClose}
            handleSubmit={onClose}
            isEditShow={false}
            modalStatus='View'
            isUnique
            editTitle='Done'
            valueChanged={false}
          />
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            void handleFormSubmit(values);
          }}>
          {({ touched, handleChange, errors, values, setFieldValue }) => {
            const computedEndDate =
              values.expirationPreset !== 'custom' && values.expirationPreset !== 'no_expiration'
                ? calculateEffectiveTo(values.effectiveFrom, values.expirationPreset)
                : null;

            return (
              <Form id='createAdvisorApiKeyForm'>
                <ModalBodyComponent>
                  <Heading
                    variant='body-s'
                    color={COLORS.Silver}
                    className='mb-4'
                    title='Each key is valid between its effective dates until revoked. Existing active keys remain valid when you add another.'
                  />
                  <div className='grid grid-cols-1 gap-4'>
                    <CustomDatePicker
                      label='Effective From'
                      name='effectiveFrom'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      values={values}
                      setFieldValue={setFieldValue}
                      required
                    />
                    <FormSelect
                      label='Expiration'
                      name='expirationPreset'
                      options={[...EXPIRATION_PRESET_OPTIONS]}
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      values={values}
                      setFieldValue={(field, value) => {
                        void setFieldValue(field, value);
                        if (value !== 'custom') {
                          void setFieldValue('effectiveTo', '');
                        }
                      }}
                      required
                    />
                    {values.expirationPreset === 'custom' && (
                      <CustomDatePicker
                        label='End Date'
                        name='effectiveTo'
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                        values={values}
                        setFieldValue={setFieldValue}
                        required
                      />
                    )}
                    {computedEndDate && (
                      <Heading
                        variant='body-xs'
                        color={COLORS.SpanishGray}
                        title={`This key will expire on ${moment(computedEndDate).format('MMM D, YYYY')}.`}
                      />
                    )}
                    {values.expirationPreset === 'no_expiration' && (
                      <Heading
                        variant='body-xs'
                        color={COLORS.SpanishGray}
                        title='This key will not expire automatically. Revoke it manually when it is no longer needed.'
                      />
                    )}
                  </div>
                </ModalBodyComponent>
                <ModalFooterComponent
                  onCloseModal={onClose}
                  isEditShow={false}
                  modalStatus='Add'
                  buttonTitle='Generate Key'
                  isLoading={isBusy}
                />
              </Form>
            );
          }}
        </Formik>
      )}
    </ModalComponent>
  );
};

export default CreateAdvisorApiKeyModal;
